


export function toHumanReadableTime(time: number): string {
    if (time < 1000) {
        return `${time} ms`;
    } else if (time < 60 * 1000) {
        return `${(time / 1000).toFixed(2)} s`;
    } else if (time < 60 * 60 * 1000) {
        return `${(time / (60 * 1000)).toFixed(2)} min`;
    } else if (time < 24 * 60 * 60 * 1000) {
        return `${(time / (60 * 60 * 1000)).toFixed(2)} h`;
    }
    else if (time < 7 * 24 * 60 * 60 * 1000) {
        return `${(time / (24 * 60 * 60 * 1000)).toFixed(2)} d`;
    }
    else if (time < 30 * 24 * 60 * 60 * 1000) {
        return `${(time / (7 * 24 * 60 * 60 * 1000)).toFixed(2)} w`;
    }
    else if (time < 365 * 24 * 60 * 60 * 1000) {
        return `${(time / (30 * 24 * 60 * 60 * 1000)).toFixed(2)} mo`;
    }
    else {
        return `${(time / (365 * 24 * 60 * 60 * 1000)).toFixed(2)} y`;
    }
}

export function toHumanReadableTimePassedSinceNow(pastTimestamp: number, postfix: string = "ago"): string {
    const now = Date.now();
    const diff = now - pastTimestamp;
    // Anything under half a minute reads as "just now" — millisecond/second
    // precision ("7 ms ago") is noise for a human timestamp.
    if (diff < 30 * 1000) {
        return "just now";
    }
    let humanReadableTime = _formatTimeDifference(diff);
    if (postfix) {
        return `${humanReadableTime} ${postfix}`;
    }
    return humanReadableTime;
}


function _formatTimeDifference(diff: number): string {
    if (diff < 1000) {
        return `${diff} ms`;
    } else if (diff < 60 * 1000) {
        return `${Math.floor(diff / 1000)} sec`;
    } else if (diff < 60 * 60 * 1000) {
        return `${Math.floor(diff / (60 * 1000))}min`;
    } else if (diff < 24 * 60 * 60 * 1000) {
        return `${Math.floor(diff / (60 * 60 * 1000))} hour`;
    }
    else if (diff < 7 * 24 * 60 * 60 * 1000) {
        return `${Math.floor(diff / (24 * 60 * 60 * 1000))} day`;
    }
    else if (diff < 30 * 24 * 60 * 60 * 1000) {
        return `${Math.floor(diff / (7 * 24 * 60 * 60 * 1000))} week`;
    }
    else if (diff < 365 * 24 * 60 * 60 * 1000) { // less than a year
        return `${Math.floor(diff / (30 * 24 * 60 * 60 * 1000))} month`;
    }
    else {
        return `A long time ago`;
    }
}



type HumanReadableOptions = {
    round?: boolean;
    ceil?: boolean;
}
export function toHumanReadableBytes(bytes: number, decimals: number = 2, options?: HumanReadableOptions): string {
    if (typeof bytes !== 'number' || isNaN(bytes)) {
        return "N/A";
    }
    if (bytes < 1024) {
        return `${applyOptions(bytes, options)} B`;
    } else if (bytes < 1024 * 1024) {
        return `${applyOptions((bytes / 1024), options).toFixed(decimals)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
        return `${applyOptions((bytes / (1024 * 1024)), options).toFixed(decimals)} MB`;
    } else {
        return `${applyOptions((bytes / (1024 * 1024 * 1024)), options).toFixed(decimals)} GB`;
    }
}
function applyOptions(value: number, options?: HumanReadableOptions): number {
    if (options?.round) {
        return Math.round(value);
    }
    else if (options?.ceil) {
        return Math.ceil(value);
    }
    return value;
}


export function toMaxFixed(value: number, maxDecimals: number = 2): string {
    const fixed = value.toFixed(maxDecimals);
    return String(parseFloat(fixed));
}

/**
 * Compact, human-readable COUNT: "1.6M", "17.9k", "312". One decimal in the k/M
 * range (trailing ".0" dropped); counts below 1000 stay exact. For labelled counts
 * in the UI (triangles, vertices, faces…) — NOT bytes (see toHumanReadableBytes).
 */
export function toHumanReadableCount(count: number, decimals: number = 1): string {
    if (typeof count !== "number" || isNaN(count)) return "N/A";
    const abs = Math.abs(count);
    if (abs >= 1_000_000) return `${toMaxFixed(count / 1_000_000, decimals)}M`;
    if (abs >= 1_000) return `${toMaxFixed(count / 1_000, decimals)}k`;
    return toMaxFixed(count, 2);
}

/**
 * Turn an identifier into a human-readable display name — Unity's "nicify"
 * behaviour: `BasicIKConstraint` → `Basic IK Constraint`.
 *
 * Words are split where a new word actually STARTS, so acronyms stay intact:
 * a run of capitals is kept together and only broken before the capital that
 * begins the next word (`ARBackground` → `AR Background`, not `A R Background`).
 * Separators (`_`, `-`, `.`, whitespace) become spaces, digits stay attached to
 * the word they follow (`OcclusionN8` → `Occlusion N8`), and the result is
 * capitalised.
 *
 * Purely presentational: the raw identifier stays the id/lookup key, so search
 * and tooling must keep matching against BOTH.
 */
export function toHumanReadableName(name: string): string {
    if (!name) return "";
    return name
        // separators (incl. a Unity-style `m_` prefix) → spaces
        .replace(/^m_/, "")
        .replace(/[_\-.]+/g, " ")
        // a lowercase letter or digit followed by a capital starts a new word
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        // ...as does the LAST capital of a run that is followed by a lowercase
        // letter — this is what keeps acronyms whole (IK, XR, AR, USDZ)
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/^./, c => c.toUpperCase());
}
